import { useEffect, useState } from "react";
import { api, ApiError } from "../../lib/api";
import { Modal } from "../ui/modal";
import { Button } from "../ui/button";
import { Field, Input, Select } from "../ui/input";
import { useToastStore } from "../../stores/toast";
import { CRM_STATUS_LABELS, CRM_STATUS_ORDER } from "../../lib/crm";
import type { CrmAccount, CrmAccountStatus } from "@shared/types";

// Les boutons vivent dans le pied du panneau, hors du <form> : ils s'y rattachent
// par l'attribut form=, seule façon de garder la soumission native au clavier.
const FORM_ID = "crm-account-quick-create";

type Pending = "liste" | "fiche" | null;

interface AdminUser {
  id: string;
  name: string;
  role: "admin" | "user";
}

interface AccountQuickCreateProps {
  open: boolean;
  onClose: () => void;
  onCreated: (account: CrmAccount, openFiche: boolean) => void;
}

export function AccountQuickCreate({ open, onClose, onCreated }: AccountQuickCreateProps) {
  const push = useToastStore((s) => s.push);
  const [name, setName] = useState("");
  const [status, setStatus] = useState<CrmAccountStatus>("prospect");
  const [ownerUserId, setOwnerUserId] = useState("");
  const [owners, setOwners] = useState<AdminUser[]>([]);
  const [pending, setPending] = useState<Pending>(null);
  const [error, setError] = useState<string | null>(null);

  const saving = pending !== null;
  const ready = name.trim() !== "";

  // La fenêtre reste montée entre deux ouvertures : on repart d'un formulaire
  // vierge plutôt que de rouvrir sur la saisie du dossier précédent.
  useEffect(() => {
    if (!open) return;
    setName("");
    setStatus("prospect");
    setOwnerUserId("");
    setError(null);
    setPending(null);
    api<AdminUser[]>("/api/users")
      .then((list) => setOwners(list.filter((u) => u.role === "admin")))
      .catch(() => setOwners([]));
  }, [open]);

  const create = async (openFiche: boolean) => {
    const trimmed = name.trim();
    if (!trimmed || saving) return;

    setPending(openFiche ? "fiche" : "liste");
    setError(null);
    try {
      // Le reste du dossier a des valeurs par défaut côté API : on n'envoie que
      // ce qui a été saisi, un champ omis vaut mieux qu'une chaîne vide.
      const account = await api<CrmAccount>("/api/crm/accounts", {
        method: "POST",
        body: JSON.stringify({
          name: trimmed,
          status,
          ...(ownerUserId ? { ownerUserId } : {}),
        }),
      });
      push("success", "Dossier créé", account.name);
      onCreated(account, openFiche);
    } catch (err) {
      const message =
        err instanceof ApiError && err.status === 403
          ? "Le CRM est réservé aux administrateurs."
          : "Le dossier n'a pas pu être créé — réessaie dans un instant.";
      // La fenêtre reste ouverte : la saisie est courte mais on ne la jette pas.
      setError(message);
      push("danger", "Échec de la création", message);
    } finally {
      setPending(null);
    }
  };

  return (
    <Modal
      open={open}
      title="Nouveau dossier"
      onClose={onClose}
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>
            Annuler
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => void create(false)}
            disabled={saving || !ready}
          >
            {pending === "liste" ? "Création…" : "Créer"}
          </Button>
          <Button type="submit" form={FORM_ID} variant="primary" disabled={saving || !ready}>
            {pending === "fiche" ? "Création…" : "Créer et ouvrir"}
          </Button>
        </>
      }
    >
      <form
        id={FORM_ID}
        className="crm-quick"
        onSubmit={(e) => {
          e.preventDefault();
          void create(true);
        }}
      >
        <Field label="Nom du dossier">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="ex : Bistro Laurier"
            autoFocus
          />
        </Field>

        <Field label="Statut">
          <Select value={status} onChange={(e) => setStatus(e.target.value as CrmAccountStatus)}>
            {CRM_STATUS_ORDER.map((s) => (
              <option key={s} value={s}>
                {CRM_STATUS_LABELS[s]}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Responsable">
          <Select value={ownerUserId} onChange={(e) => setOwnerUserId(e.target.value)}>
            <option value="">Non attribué</option>
            {owners.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </Select>
        </Field>

        <p className="crm-quick__hint">
          C'est tout ce qu'il faut pour commencer. Le contact, l'adresse et le contrat se
          remplissent ensuite depuis la fiche, au fil de l'eau — ou s'extraient d'un seul coup d'un
          compte rendu d'appel collé.
        </p>

        {error ? (
          <p className="crm-quick__error" role="alert">
            {error}
          </p>
        ) : null}
      </form>
    </Modal>
  );
}
